<template>
  <v-dialog
    :model-value="true"
    :retain-focus="false"
    width="1200"
    max-width="calc(100vw - 48px)"
    @update:model-value="handleDialogUpdate"
  >
    <section
      class="municipality-cooperation-window"
      role="dialog"
      aria-modal="true"
      aria-labelledby="municipality-cooperation-window-title"
    >
      <header class="window-header">
        <div class="window-title">
          <v-icon
            icon="mdi-sitemap"
            color="#3ACAC9"
            size="20"
          />
          <h2 id="municipality-cooperation-window-title">
            自治体連携
          </h2>
        </div>
        <v-btn
          v-if="showDelete"
          icon="mdi-trash-can-outline"
          variant="text"
          size="small"
          color="rgba(0, 0, 0, 0.54)"
          :disabled="isSaving"
          aria-label="自治体連携を削除"
          @click="$emit('delete')"
        />
      </header>

      <div class="window-body">
        <section class="detail-section">
          <h3>同意メール</h3>
          <div class="consent-mail-grid">
            <div class="field-block">
              <span class="field-label">同意メール送信日時</span>
              <v-menu
                v-model="isConsentMailAtPickerOpen"
                :close-on-content-click="false"
                location="bottom start"
              >
                <template #activator="{ props: menuProps }">
                  <input
                    :value="form.consentMailRequestedAt"
                    v-bind="menuProps"
                    class="datetime-input"
                    type="text"
                    placeholder="YYYY/MM/DD HH:mm"
                    readonly
                  >
                </template>
                <VueDatePicker
                  v-model="form.consentMailRequestedAt"
                  locale="ja"
                  format="yyyy/MM/dd HH:mm"
                  model-type="yyyy/MM/dd HH:mm"
                  :enable-time-picker="true"
                  :clearable="true"
                  inline
                  @update:model-value="isConsentMailAtPickerOpen = false"
                />
              </v-menu>
            </div>
            <div class="field-block radio-block">
              <span class="field-label">返信有無</span>
              <label class="choice-row">
                <input
                  v-model="form.replied"
                  type="radio"
                  :value="true"
                  @click="toggleRadio('replied', true)"
                >
                <span>有り</span>
              </label>
              <label class="choice-row">
                <input
                  v-model="form.replied"
                  type="radio"
                  :value="false"
                  @click="toggleRadio('replied', false)"
                >
                <span>無し</span>
              </label>
            </div>
            <div class="field-block">
              <span class="field-label">返信日時</span>
              <v-menu
                v-model="isRepliedAtPickerOpen"
                :close-on-content-click="false"
                location="bottom start"
              >
                <template #activator="{ props: menuProps }">
                  <input
                    :value="form.repliedAt"
                    v-bind="menuProps"
                    class="datetime-input"
                    type="text"
                    placeholder="YYYY/MM/DD HH:mm"
                    readonly
                  >
                </template>
                <VueDatePicker
                  v-model="form.repliedAt"
                  locale="ja"
                  format="yyyy/MM/dd HH:mm"
                  model-type="yyyy/MM/dd HH:mm"
                  :enable-time-picker="true"
                  :clearable="true"
                  inline
                  @update:model-value="isRepliedAtPickerOpen = false"
                />
              </v-menu>
            </div>
            <div class="field-block reply-content-block">
              <span class="field-label">返信内容</span>
              <div class="reply-options">
                <label
                  v-for="option in replyContentOptions"
                  :key="option.value"
                  class="choice-row"
                >
                  <input
                    v-model="form.replyContent"
                    type="radio"
                    :value="option.value"
                    @click="toggleRadio('replyContent', option.value)"
                  >
                  <span>{{ option.label }}</span>
                </label>
              </div>
            </div>
          </div>
        </section>

        <section class="detail-section">
          <h3>連携実施</h3>
          <div class="cooperation-grid">
            <div class="field-block-group cooperated-method-group">
              <div class="field-block radio-block">
                <span class="field-label">連携実施有無</span>
                <label class="choice-row">
                  <input
                    v-model="form.cooperated"
                    type="radio"
                    :value="true"
                    @click="toggleRadio('cooperated', true)"
                  >
                  <span>有り</span>
                </label>
                <label class="choice-row">
                  <input
                    v-model="form.cooperated"
                    type="radio"
                    :value="false"
                    @click="toggleRadio('cooperated', false)"
                  >
                  <span>無し</span>
                </label>
              </div>

              <div class="field-block method-block">
                <span class="field-label">連携先</span>
                <label
                  v-for="option in partnerOptions"
                  :key="option.key"
                  class="choice-row"
                >
                  <input
                    v-model="form[option.key]"
                    type="checkbox"
                  >
                  <span>{{ option.label }}</span>
                </label>
              </div>
            </div>

            <div class="municipality-detail-fields">
              <div class="field-block">
                <span class="field-label">自治体名</span>
                <input
                  v-model="form.municipalityName"
                  class="text-input"
                  type="text"
                >
              </div>

              <div class="field-block">
                <span class="field-label">自治体電話番号</span>
                <input
                  v-model="form.municipalityTel"
                  class="text-input"
                  type="text"
                >
              </div>

              <div class="field-block">
                <span class="field-label">自治体メールアドレス</span>
                <input
                  v-model="form.municipalityEmail"
                  class="text-input"
                  type="text"
                >
              </div>

              <div class="field-block">
                <span class="field-label">自治体担当者名</span>
                <input
                  v-model="form.municipalityStaffName"
                  class="text-input"
                  type="text"
                >
              </div>

              <div class="field-block">
                <span class="field-label">連携日時</span>
                <v-menu
                  v-model="isCooperatedAtPickerOpen"
                  :close-on-content-click="false"
                  location="bottom start"
                >
                  <template #activator="{ props: menuProps }">
                    <input
                      :value="form.cooperatedAt"
                      v-bind="menuProps"
                      class="datetime-input"
                      type="text"
                      placeholder="YYYY/MM/DD HH:mm"
                      readonly
                    >
                  </template>
                  <VueDatePicker
                    v-model="form.cooperatedAt"
                    locale="ja"
                    format="yyyy/MM/dd HH:mm"
                    model-type="yyyy/MM/dd HH:mm"
                    :enable-time-picker="true"
                    :clearable="true"
                    inline
                    @update:model-value="isCooperatedAtPickerOpen = false"
                  />
                </v-menu>
              </div>
            </div>
          </div>

          <div class="cooperation-secondary-row">
            <div class="field-block identified-block">
              <span class="field-label">自治体での個人特定</span>
              <p class="first-cooperation-note">
                ※初回連携時のみ入力
              </p>
              <label
                v-for="option in individualIdentifiedOptions"
                :key="option.value"
                class="choice-row"
              >
                <input
                  v-model="form.individualIdentified"
                  type="radio"
                  :value="option.value"
                  @click="toggleRadio('individualIdentified', option.value)"
                >
                <span>{{ option.label }}</span>
              </label>
            </div>

            <div class="field-block support-status-block">
              <span class="field-label">自治体でのこれまでの支援状況</span>
              <p class="first-cooperation-note">
                ※初回連携時のみ入力
              </p>
              <label
                v-for="option in supportStatusOptions"
                :key="option.value"
                class="choice-row"
              >
                <input
                  v-model="form.supportStatus"
                  type="radio"
                  :value="option.value"
                  @click="toggleRadio('supportStatus', option.value)"
                >
                <span>{{ option.label }}</span>
                <v-tooltip
                  v-if="option.tooltip"
                  location="top"
                  max-width="360"
                >
                  <template #activator="{ props: tipProps }">
                    <v-icon
                      v-bind="tipProps"
                      icon="mdi-help-circle-outline"
                      size="16"
                      color="#9e9e9e"
                    />
                  </template>
                  <ul class="tooltip-list">
                    <li
                      v-for="(line, index) in option.tooltip"
                      :key="index"
                    >
                      {{ line }}
                    </li>
                  </ul>
                </v-tooltip>
              </label>
            </div>

            <div class="field-block reaction-block">
              <span class="field-label">自治体の反応</span>
              <textarea
                v-model="form.municipalityReaction"
                class="editable-note"
              />
            </div>
          </div>
        </section>

        <section class="detail-section supplement-section">
          <h3>対応補足</h3>
          <p class="warning-text">
            ※ 相談員に表示される項目のため、氏名、電話番号、居住地域などの個人情報は入力しないでください
          </p>
          <textarea
            v-model="form.note"
            class="editable-note supplement-note"
          />
        </section>
      </div>

      <footer class="window-actions">
        <v-btn
          variant="outlined"
          min-width="112"
          :disabled="isSaving"
          @click="$emit('cancel')"
        >
          キャンセル
        </v-btn>
        <v-btn
          color="#3ACAC9"
          variant="flat"
          min-width="88"
          :disabled="isSaving"
          :loading="isSaving"
          @click="save"
        >
          保存
        </v-btn>
      </footer>
    </section>
  </v-dialog>
</template>

<script>
import { toggleRadioSelection } from "@/vue/shared/utils/deselectableRadio";

// 連携先は複数選択可。key は municipal_cooperations の partner_* カラムに対応する。
const partnerOptions = [
  { key: "partnerMunicipality", param: "partner_municipality", label: "自治体" },
  { key: "partnerChildGuidanceCenter", param: "partner_child_guidance_center", label: "児童相談所" },
  { key: "partnerPolice", param: "partner_police", label: "警察" },
  { key: "partnerHospital", param: "partner_hospital", label: "病院" },
  { key: "partnerOther", param: "partner_other", label: "その他" }
];

const replyContentOptions = [
  { value: 1, label: "同意あり" },
  { value: 2, label: "不要（自力で対応可能）" },
  { value: 3, label: "相談済み（自治体・病院等）" },
  { value: 4, label: "相談済み（家族等）" },
  { value: 5, label: "メール送信不能" },
  { value: 6, label: "理由不明の断り" },
  { value: 7, label: "明確な拒否" }
];

const individualIdentifiedOptions = [
  { value: 1, label: "特定された" },
  { value: 2, label: "不明（情報提供不可）" },
  { value: 3, label: "不明（その他）" }
];

const supportStatusOptions = [
  { value: 1, label: "未把握" },
  {
    value: 2,
    label: "把握済み（通常支援）",
    tooltip: [
      "次回の乳幼児健診時等（対象者が全住民のイベント）の機会にフォロー予定であった",
      "当該児童（胎児含む）の兄弟姉妹への個別フォローは行なっていたが、当該児童（今回の妊娠含む）の個別フォローは行なっていない",
      "過去には個別フォローを行なっていたが、現在は落ち着き通常支援となっている"
    ]
  },
  {
    value: 3,
    label: "把握済み（積極的介入・支援あり）",
    tooltip: [
      "「要支援妊産婦」「要保護児童（特定妊婦など）」であり要保護児童対策地域協議会のリストにあがっている",
      "電話や訪問による個別フォローがある（サポートプランを作成し支援中）"
    ]
  },
  { value: 4, label: "不明" }
];

export default {
  name: "FaceSheetMunicipalityCooperationDetailWindow",
  props: {
    cooperation: {
      type: Object,
      required: true
    },
    // 保存処理中フラグ。true の間は保存ボタンを無効化し連打を防ぐ。
    isSaving: {
      type: Boolean,
      default: false
    },
    // 削除ボタンを表示するかどうか（既存レコードを開いたときのみ true）。
    showDelete: {
      type: Boolean,
      default: false
    }
  },
  emits: ["cancel", "save", "delete"],
  data() {
    return {
      partnerOptions,
      replyContentOptions,
      individualIdentifiedOptions,
      supportStatusOptions,
      form: this.createForm(this.cooperation),
      isConsentMailAtPickerOpen: false,
      isRepliedAtPickerOpen: false,
      isCooperatedAtPickerOpen: false
    };
  },
  watch: {
    cooperation: {
      handler(cooperation) {
        this.form = this.createForm(cooperation);
      },
      immediate: true
    }
  },
  methods: {
    createForm(cooperation) {
      return {
        consentMailRequestedAt: cooperation?.consentMailRequestedAt || cooperation?.consentMailSentAt || "",
        // 返信有無・連携実施有無は未入力（null）を保持するため false へ丸めない。
        replied: cooperation?.replied ?? null,
        repliedAt: cooperation?.repliedAt || "",
        replyContent: cooperation?.replyContent ?? null,
        cooperated: cooperation?.cooperated ?? null,
        ...Object.fromEntries(partnerOptions.map((option) => [option.key, cooperation?.[option.key] === true])),
        municipalityName: cooperation?.municipalityName || "",
        municipalityTel: cooperation?.municipalityTel || "",
        municipalityEmail: cooperation?.municipalityEmail || "",
        municipalityStaffName: cooperation?.municipalityStaffName || "",
        cooperatedAt: cooperation?.cooperatedAt || "",
        individualIdentified: cooperation?.individualIdentified ?? null,
        supportStatus: cooperation?.supportStatus ?? null,
        municipalityReaction: cooperation?.municipalityReaction || "",
        note: cooperation?.note || ""
      };
    },
    toggleRadio(key, value) {
      toggleRadioSelection(this.form, key, value);
    },
    handleDialogUpdate(value) {
      // 保存処理中（isSaving=true）は外側クリック・Escによる閉じる操作を無視する。
      if (this.isSaving) return;
      if (!value) this.$emit("cancel");
    },
    save() {
      this.$emit("save", {
        id: this.cooperation?.id,
        lockVersion: this.cooperation?.lockVersion,
        consent_mail_requested_at: this.form.consentMailRequestedAt || null,
        replied: this.form.replied,
        replied_at: this.form.repliedAt || null,
        reply_content: this.form.replyContent,
        cooperated: this.form.cooperated,
        ...Object.fromEntries(partnerOptions.map((option) => [option.param, this.form[option.key]])),
        municipality_name: this.form.municipalityName,
        municipality_tel: this.form.municipalityTel,
        municipality_email: this.form.municipalityEmail,
        municipality_staff_name: this.form.municipalityStaffName,
        cooperated_at: this.form.cooperatedAt || null,
        individual_identified: this.form.individualIdentified,
        support_status: this.form.supportStatus,
        municipality_reaction: this.form.municipalityReaction,
        note: this.form.note
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.municipality-cooperation-window {
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 48px);
  position: relative;
  width: 100%;
  z-index: 1;
}

.window-header {
  align-items: center;
  border-bottom: 1px solid #eeeeee;
  display: flex;
  justify-content: space-between;
  min-height: 48px;
  padding: 0 20px;
}

.window-title {
  align-items: center;
  display: flex;
  gap: 8px;
}

.window-title h2 {
  color: rgba(0, 0, 0, 0.78);
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  margin: 0;
}

.window-body {
  overflow-y: auto;
  overflow-x: visible;
}

.detail-section {
  border-bottom: 1px solid #eeeeee;
  padding: 12px 20px 16px;
  position: relative;
  z-index: 1;
}

.detail-section h3 {
  color: rgba(0, 0, 0, 0.82);
  font-size: 13px;
  font-weight: 700;
  margin: 0 0 10px;
}

.consent-mail-grid,
.cooperation-grid {
  display: grid;
  gap: 14px;
}

.consent-mail-grid {
  grid-template-columns: 170px 76px 170px 1fr;
}

.cooperation-grid {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

.field-block-group {
  display: flex;
  gap: 48px;
  grid-column: span 2;
  min-width: 0;
}

.field-block-group .field-block {
  flex: 0 0 auto;
}

.field-block-group .radio-block {
  min-width: 96px;
}

/* 自治体名・電話番号・メールアドレスを列１に、3行、担当者名・連携日時を列２に2行で整列する。 */
.municipality-detail-fields {
  display: grid;
  grid-column: span 4;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(3, auto);
  grid-auto-flow: column;
  gap: 14px;
}

.field-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.field-label {
  color: rgba(0, 0, 0, 0.58);
  font-size: 12px;
  line-height: 1.2;
}

.choice-row,
.warning-text {
  color: rgba(0, 0, 0, 0.76);
  font-size: 12px;
  line-height: 1.45;
}

.choice-row {
  align-items: center;
  align-self: flex-start;
  display: flex;
  gap: 4px;
}

.choice-row input {
  height: 13px;
  margin: 0;
  width: 13px;
}

.reply-options {
  column-gap: 14px;
  display: grid;
  grid-template-columns: repeat(2, max-content);
  row-gap: 4px;
}

.reply-options .choice-row {
  justify-self: start;
}

.cooperation-secondary-row {
  align-items: flex-start;
  display: flex;
  gap: 32px;
  margin-top: 28px;
}

.identified-block,
.support-status-block {
  flex: 0 0 auto;
}

.reaction-block {
  flex: 1;
  min-width: 200px;
}

.tooltip-list {
  margin: 0;
  padding-left: 18px;
}

.tooltip-list li {
  font-size: 12px;
  line-height: 1.5;
}

.text-input,
.editable-note,
.datetime-input {
  background-color: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 3px;
  color: rgba(0, 0, 0, 0.72);
  font-size: 12px;
  line-height: 1.5;
  padding: 6px 8px;
  width: 100%;
}

.text-input,
.datetime-input {
  height: 30px;
}

.datetime-input {
  cursor: pointer;
}

.editable-note {
  min-height: 70px;
  resize: vertical;
}

.warning-text {
  color: #ef5d7a;
  font-weight: 700;
  margin: 0 0 8px;
}

.first-cooperation-note {
  color: #ef5d7a;
  font-size: 11px;
  line-height: 1.4;
  margin: 0 0 2px;
}

.supplement-section {
  padding-bottom: 22px;
}

.supplement-note {
  min-height: 80px;
}

.window-actions {
  align-items: center;
  background-color: #fafafa;
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  padding: 16px 20px;
}

@media (max-width: 900px) {
  .consent-mail-grid,
  .cooperation-grid {
    gap: 16px;
    grid-template-columns: 1fr 1fr;
  }

  .reply-content-block {
    grid-column: 1 / -1;
  }

  .cooperation-secondary-row {
    flex-wrap: wrap;
  }
}

@media (max-width: 640px) {
  .consent-mail-grid,
  .cooperation-grid,
  .reply-options {
    grid-template-columns: 1fr;
  }

  .municipality-detail-fields {
    grid-template-columns: 1fr;
    grid-template-rows: none;
    grid-auto-flow: row;
  }

  .cooperation-secondary-row {
    flex-direction: column;
  }
}
</style>
